import { isHourTrackingEntryType } from './employeeTimeEntryHours';
import { addDaysYMD } from './date';
import { normalizeChangeRequestDate } from './pendingChangeRequests';

export type GroupableChangeRequest = {
  id: string;
  employee_id: string;
  employee_name: string;
  entry_date: string;
  action: 'upsert' | 'delete';
  proposed_payload: Record<string, unknown> | null;
  previous_snapshot: Record<string, unknown> | null;
  requester_name: string;
  created_at: string;
  /** Appointment the request belongs to (clock mode `activity`). */
  activity_title?: string | null;
  /** Project the request belongs to (project time without appointment). */
  lead_title?: string | null;
};

export type ChangeRequestGroup<T extends GroupableChangeRequest = GroupableChangeRequest> = {
  ids: string[];
  items: T[];
  fromDate: string;
  toDate: string;
  dayCount: number;
};

/** Consecutive inserts from one Field range request stay well under this. */
const CREATED_AT_NEIGHBOR_MS = 30_000;

function payloadKey(item: GroupableChangeRequest): string {
  if (item.action === 'delete') return 'delete';
  const p = item.proposed_payload ?? {};
  const type = String(p.entry_type ?? '').trim();
  const note = String(p.note ?? '').trim();
  const start = String(p.start_time ?? '').trim();
  const end = String(p.end_time ?? '').trim();
  const brk = String(p.break_minutes ?? '').trim();
  const activity = String(p.activity_id ?? '').trim();
  const lead = String(p.lead_id ?? '').trim();
  return `${type}|${note}|${start}|${end}|${brk}|${activity}|${lead}`;
}

function clusterKey(item: GroupableChangeRequest): string {
  return `${item.employee_id}|${item.requester_name}|${item.action}|${payloadKey(item)}`;
}

function createdAtMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

function isRangeGroupable(item: GroupableChangeRequest): boolean {
  if (item.action !== 'upsert') return false;
  const type = String(item.proposed_payload?.entry_type ?? 'work').trim();
  return Boolean(type) && !isHourTrackingEntryType(type);
}

function toGroup<T extends GroupableChangeRequest>(items: T[]): ChangeRequestGroup<T> {
  const dates = items.map((item) => normalizeChangeRequestDate(item.entry_date)).sort();
  const fromDate = dates[0] ?? '';
  const toDate = dates[dates.length - 1] ?? fromDate;
  return {
    ids: items.map((item) => item.id),
    items,
    fromDate,
    toDate,
    dayCount: items.length,
  };
}

/**
 * Collapse consecutive all-day absence requests (vacation/sickness, …) from one
 * Field range submit into a single row. Work / delete stay one row per day.
 */
export function groupTimeEntryChangeRequests<T extends GroupableChangeRequest>(
  items: T[],
): ChangeRequestGroup<T>[] {
  const singletons: T[] = [];
  const groupable: T[] = [];
  for (const item of items) {
    if (isRangeGroupable(item)) groupable.push(item);
    else singletons.push(item);
  }

  groupable.sort((a, b) => {
    const key = clusterKey(a).localeCompare(clusterKey(b));
    if (key !== 0) return key;
    const date = normalizeChangeRequestDate(a.entry_date).localeCompare(
      normalizeChangeRequestDate(b.entry_date),
    );
    if (date !== 0) return date;
    return createdAtMs(a.created_at) - createdAtMs(b.created_at);
  });

  const grouped: T[][] = [];
  for (const item of groupable) {
    const prevRun = grouped[grouped.length - 1];
    const prev = prevRun?.[prevRun.length - 1];
    if (
      prev &&
      clusterKey(prev) === clusterKey(item) &&
      addDaysYMD(normalizeChangeRequestDate(prev.entry_date), 1) ===
        normalizeChangeRequestDate(item.entry_date) &&
      Math.abs(createdAtMs(item.created_at) - createdAtMs(prev.created_at)) <= CREATED_AT_NEIGHBOR_MS
    ) {
      prevRun.push(item);
    } else {
      grouped.push([item]);
    }
  }

  const groups = [...grouped.map((run) => toGroup(run)), ...singletons.map((item) => toGroup([item]))];
  groups.sort((a, b) => {
    const aCreated = Math.max(...a.items.map((item) => createdAtMs(item.created_at)));
    const bCreated = Math.max(...b.items.map((item) => createdAtMs(item.created_at)));
    if (bCreated !== aCreated) return bCreated - aCreated;
    return a.fromDate.localeCompare(b.fromDate);
  });
  return groups;
}
